import React from 'react';
import kebabCase from 'lodash/kebabCase';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import { IAvailableIconOptions } from '../IconSamples';
import Image, { IImage } from 'tbk-components/src/components/Image';
import AnimatedArcs from '../AnimatedArcs';
import Button, { ILink } from 'tbk-components/src/components/Button';

export interface IServiceItem {
	serviceItemHeading?: string;
	link?: ILink;
	/**
	 * @control iconselect
	 */
	serviceIcon?: IAvailableIconOptions['calianIconSelectValues'];
	hideIcon?: boolean;
	/**
	 * @control textarea
	 */
	serviceItemDescription?: string;
}

export interface IServiceListingWithIconsProps extends IBaseElementProps {
	heading?: string;
	/**
	 * Heading Icon Images - To use to include full color icon images if needed.
	 */
	headingIconImages: IImage[];
	/**
	 * @control textarea
	 */
	description?: string;
	buttonLink?: ILink;
	serviceItems?: IServiceItem[];
	serviceIconColorTheme?: 'Primary' | 'Defence Primary' | 'AT Primary';
	ctaImage?: IImage;
	ctaHeading?: string;
	ctaDescription?: string;
	ctaLink?: ILink;
	/**
	 * Enable Line Animations
	 */
	enableLineAnimations?: boolean;
	/**
	 * Line Animation Delay in Milliseconds
	 */
	lineAnimationDelay?: number;
	/**
	 * Line Animation Offset in Pixels
	 */
	lineAnimationOffset?: number;
	/**
	 * Arc One Color - HEX Value
	 */
	arcOneColor?: string;
	/**
	 * Arc One Stroke Width
	 * Default: 3
	 */
	arcOneStrokeWidth?: number;
	/**
	 * Arc Two Color - HEX Value
	 */
	arcTwoColor?: string;
	/**
	 * Arc Two Stroke Width
	 * Default: 3
	 */
	arcTwoStrokeWidth?: number;
}

/**
 * Service Listing With Icons
 * @block
 * @icon list-view
 */
const ServiceListingWithIcons: React.FC<IServiceListingWithIconsProps> = ({
	className,
	classNames = [],
	heading,
	headingIconImages,
	description,
	buttonLink,
	serviceItems,
	serviceIconColorTheme = 'Primary',
	ctaImage,
	ctaHeading,
	ctaDescription,
	ctaLink,
	enableLineAnimations = true,
	lineAnimationDelay = 250,
	lineAnimationOffset = 150,
	arcOneColor = 'var(--bs-white)',
	arcOneStrokeWidth = 4,
	arcTwoColor = 'var(--bs-white)',
	arcTwoStrokeWidth = 3,
	id,
}) => {
	return (
		<div
			id={id}
			className={
				className ||
				cn(
					'calian-service-listing-with-icons overflow-hidden bg-light-bg position-relative',
					...classNames,
				)
			}
		>
			<div className="container-fluid mw-1440px pt-6 pb-4 pt-lg-12 pb-lg-10 ps-3 pe-3 ps-xl-14 pe-xl-8 position-relative z-1">
				<div className="row mb-lg-7 pb-lg-2">
					<div
						className={cn(
							'col-12',
							(description || buttonLink?.label) && 'col-lg-4',
						)}
					>
						{heading && <h2>{heading}</h2>}
						{headingIconImages && headingIconImages.length > 0 && (
							<div className="calian-service-listing-with-icons__heading-images d-flex flex-row justify-content-start align-items-center mt-4 mb-4 mb-lg-0">
								{headingIconImages.map((image, index) => (
									<Image
										key={index}
										{...image}
										className="img-fluid w-28px h-28px object-fit-cover rounded-circle me-4"
									/>
								))}
							</div>
						)}
					</div>
					{(description || buttonLink?.label) && (
						<div className="col-12 col-lg-8 ps-lg-2 pe-lg-10">
							{description && (
								<p className="body-lg ps-lg-1 ls-0">
									{description}
								</p>
							)}
							{buttonLink?.label && buttonLink?.href && (
								<Button
									size="sm"
									className="btn btn-primary btn-sm mb-5"
									{...buttonLink}
								/>
							)}
						</div>
					)}
				</div>

				{serviceItems && serviceItems.length > 0 && (
					<div className="row calian-service-listing-with-icons__service-grid">
						{serviceItems.map((item, index) => {
							const itemRender = (
								<>
									<div className="d-flex flex-row align-items-start mb-2">
										{item.serviceIcon && !item.hideIcon && (
											<i
												key={index}
												className={`fs-26px me-3 ps-1 icon icon-${kebabCase(
													item.serviceIcon,
												)} text-${kebabCase(
													serviceIconColorTheme,
												)}`}
											/>
										)}
										{item.serviceItemHeading && (
											<h5 className="mb-0 pe-5">
												{item.serviceItemHeading}
											</h5>
										)}
									</div>

									{item.serviceItemDescription && (
										<p className="mb-3 ls-0">
											{item.serviceItemDescription}
										</p>
									)}
								</>
							);
							return (
								<div
									key={index}
									className="col-12 col-md-6 col-lg-4 mb-1 mb-md-3 pb-1"
								>
									{item?.link?.href ? (
										<Button
											target={item?.link?.target}
											href={item?.link?.href}
											id={`service-listing-link-${index}`}
											className="service-listing-block-link text-decoration-none"
										>
											{itemRender}
										</Button>
									) : (
										itemRender
									)}
								</div>
							);
						})}
					</div>
				)}

				{(ctaImage ||
					ctaHeading ||
					ctaDescription ||
					ctaLink?.href) && (
					<div className="calian-service-listing-with-icons__cta bg-white overflow-hidden mt-3 mt-md-6 mt-xl-9">
						<div className="calian-service-listing-with-icons__cta-container d-grid pb-5 pb-md-0">
							{ctaImage && (
								<div className="calian-service-listing-with-icons__cta-image">
									<Image {...ctaImage} />
								</div>
							)}
							{(ctaHeading || ctaDescription) && (
								<div className="calian-service-listing-with-icons__cta-content px-3 px-md-0 py-md-5">
									<p className="h4">{ctaHeading}</p>
									<p>{ctaDescription}</p>
								</div>
							)}
							{ctaLink?.label && ctaLink?.href && (
								<div className="calian-service-listing-with-icons__cta-link px-3 ps-md-0 pe-md-5 py-md-5 mt-3 mt-md-0">
									<Button size="sm" {...ctaLink} />
								</div>
							)}
						</div>
					</div>
				)}
			</div>
			{enableLineAnimations && (
				<AnimatedArcs
					arcsStyle={'Component - Two Lines'}
					arcsPosition="Right"
					arcOneColor={arcOneColor}
					arcOneStrokeWidth={arcOneStrokeWidth}
					arcTwoColor={arcTwoColor}
					arcTwoStrokeWidth={arcTwoStrokeWidth}
					animationDelay={lineAnimationDelay}
					animationOffset={lineAnimationOffset}
				/>
			)}
		</div>
	);
};

export default ServiceListingWithIcons;
